import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import $ from 'jquery';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { FeatureToggle } from '@Enums';
import { SharedModule } from '@Shared/shared.module';
import { MultiSelectModel } from 'app/components/multi-select-dropdown-list/models/MultiSelectModel.interface';
import { MultiSelectDropdownListComponent } from 'app/components/multi-select-dropdown-list/multi-select-dropdown-list.component';
import { MrsColor, StorageKeysEnum } from 'app/enums';
import { ActionsEntity, TableColumnDefinition } from 'app/shared/components/mrs-table/models/table-models';
import { FeatureManagerService, TeamsService } from 'app/services';
import { CreateTeamComponent } from '../create-team/create-team.component';

@Component({
  selector: 'manage-teams-table',
  templateUrl: './teams-table.component.html',
  styleUrls: ['./app-teams.component.scss'],
  imports: [SharedModule, MultiSelectDropdownListComponent]
})
export class teamsTableComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  private subs = new SubSink();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild(MultiSelectDropdownListComponent) selectList: MultiSelectDropdownListComponent;
  @Input() data: any;
  @Input() displayedColumns: TableColumnDefinition[];
  @Input() searchableFields: string[];
  @Input() showClientFilter = false;
  @Input() clients: MultiSelectModel[];
  @Input() initialSort: string;
  @Input() addRecordAction: ActionsEntity;
  @Input() mode: string;
  @Output() tableEvent = new EventEmitter<any>();
  @Output() teamCreated = new EventEmitter<boolean>();

  private hasInactiveTeamSub = new Subject<boolean>();
  hasInactiveTeams$ = this.hasInactiveTeamSub.asObservable();

  selectedClientStorageKey = StorageKeysEnum.SelectedClients;
  searchValue = '';
  mrsDataSource = new MatTableDataSource<any>();
  displayedColumnsDef: string[];
  showActionsColumn = true;

  userProfileV2: boolean;
  filterValue = '';
  showInactiveTeams = true;
  hasInactiveTeams: boolean;
  showInactiveTeamsFormGroup: UntypedFormGroup;
  globalFilter: any = {
    clientIds: [],
    showInactiveTeams: false
  };
  createTeamDialogRef: MatDialogRef<CreateTeamComponent>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly featureManagerService: FeatureManagerService,
    private readonly teamService: TeamsService,
    private dialog: MatDialog
  ) {
    this.showInactiveTeamsFormGroup = fb.group({
      hasInactiveTeamsCtrl: false
    });
  }

  ngAfterViewInit(): void {
    this.showHideInactiveTeams();
  }

  setStyles() {
    setTimeout(() => {
      const elementCollection = Array.from(document.querySelectorAll('.name-line'));
      elementCollection?.forEach(item => {
        if (item.innerHTML.includes('(inactive)')) {
          item.closest('tr').style.backgroundColor = MrsColor.DisabledTableColumn;
          const originalHtml = item.innerHTML;
          const newHtml = originalHtml.replace('(inactive)', `<span style="color:${MrsColor.BrandRedAccent};">(inactive)</span>`);
          item.innerHTML = newHtml;
        }
      });
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue !== changes.data?.previousValue && changes.data?.currentValue !== null) {
      this.setData();
      this.setStyles();
    }
  }

  ngOnInit() {
    this.userProfileV2 = this.featureManagerService.getByName(FeatureToggle.GlobalUsersUseUserProfileV2).enabled;
    $('#sidenav').removeClass('hidenav');
    this.subs.sink = this.showInactiveTeamsFormGroup.valueChanges
      .pipe(
        tap(() => {
          const hasInactiveTeams = this.data.some((user: { disabled: boolean; }) => user.disabled);
          this.hasInactiveTeamSub.next(hasInactiveTeams);
        })
      )
      .subscribe(() => this.showHideInactiveTeams());

    this.globalFilter.searchableFields = this.searchableFields;
  }

  setData() {
    this.hasInactiveTeams = this.data.filter(x => x.disabled)?.length > 0;
    this.hasInactiveTeamSub.next(this.hasInactiveTeams);
    this.mrsDataSource.sortingDataAccessor = (item, property) => {
      const def = this.displayedColumns.find(x => x.def === property);

      if (def.sortFields && def.sortFields.length > 0) {
        let val = '';
        def.sortFields.forEach(element => {
          val += item[element];
        });

        return val;
      } else {
        return item[property];
      }
    };

    const actionColumn = this.displayedColumns.find(x => x.isAction);
    if (actionColumn) {
      this.data.forEach(element => {
        actionColumn.actions.forEach(action => {
          if (action.permission) {
            this.showActionsColumn = true;
          }

          let showAction = true;
          action.conditions?.forEach(condition => {
            if (element[condition.showProperty] !== condition.showValue) {
              showAction = false;
            }
          });

          element[action.actionType] = showAction;
        });
      });
    }

    this.mrsDataSource.paginator = this.paginator;
    this.mrsDataSource.sort = this.sort;
    this.mrsDataSource.data = this.data;
    this.mrsDataSource.filterPredicate = this.tableFilter();

    if (!this.showActionsColumn) {
      this.displayedColumns.splice(this.displayedColumns.indexOf(actionColumn), 1);
    }
    this.displayedColumnsDef = this.displayedColumns.map(x => x.def);
    this.updateFilteredCases();
  }

  updateFilteredCases() {
    this.globalFilter.filterValue = this.filterValue.trim().toLowerCase();
    this.mrsDataSource.filter = JSON.stringify(this.globalFilter);
    this.setStyles();
  }

  tableFilter(): (data: any, filter: any) => boolean {
    return function (data, filter,): boolean {
      const globalFilter = JSON.parse(filter);

      let dataFound = false;
      globalFilter?.searchableFields?.forEach(field => {
        if (data[field] && data[field]?.toLowerCase().indexOf(globalFilter.filterValue) !== -1) {
          dataFound = true;
        }
      });

      let clientIdFound = false;
      if (globalFilter.clientIds?.includes('00000000-0000-0000-0000-0000000000000') || globalFilter.clientIds?.length === 0) {
        clientIdFound = true;
      } else {
        clientIdFound = globalFilter.clientIds.some(r => data.clientIds.indexOf(r) >= 0);
      }

      let showInactiveTeams = true;
      if (!globalFilter.showInactiveTeams && data.disabled) {
        showInactiveTeams = false;
      }
      return dataFound && clientIdFound && showInactiveTeams;
    };
  }

  showHideInactiveTeams() {
    const selected = this.showInactiveTeamsFormGroup.get('hasInactiveTeamsCtrl').value;
    this.globalFilter.showInactiveTeams = selected;
    this.updateFilteredCases();
  }

  filterData(selectedClients: string[]) {
    const clientIds = selectedClients;
    this.globalFilter.clientIds = clientIds;
    this.updateFilteredCases();
  }

  tableAction(columnAction, selectedRecord) {

    if(columnAction?.actionType === 'view') {
        this.teamService.routeToTeam(selectedRecord);
    }

    this.tableEvent.emit({
      action: columnAction,
      record: selectedRecord
    });
  }

  onRowClicked(row) {
    var teamForClickedRow = {
      id: row.id,
      name: row.name,
      clientId: row.clientId
    };
    this.teamService.routeToTeam(teamForClickedRow);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  createTeam() {
    this.createTeamDialogRef = this.dialog.open(CreateTeamComponent, {
        width: '80%',
        maxWidth: '600px',
        height: 'auto',        
        maxHeight: '90vh',
        data : {
          teams: this.data
        }
    });

    this.createTeamDialogRef.componentInstance.changesSaved.subscribe((saved: boolean) => {
      if (saved) {
        this.teamCreated.emit(saved);
      }
    });
  }
}
